import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SortingState } from '@tanstack/react-table';
// `Map` is aliased: lucide's icon would shadow the global the cascade lookup is built with.
import { CalendarCheck, Map as MapIcon, Star, Upload, UserCheck, UserX, Wrench, X } from 'lucide-react';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { DataTable } from 'components/DataTable';
import { EmptyState } from 'components/EmptyState';
import { useToast } from 'components/ui/toast';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import AppContext from 'contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import { apiErrorMessage } from 'utils/errorMessage';
import { useTechniciansList } from 'hooks/useTechnicians';
import { useCountries, useSubdivisions } from 'hooks/useReferentials';
import { AVAILABILITY_STATES, type AvailabilityState } from 'api/unavailabilitiesApi';
import {
  bulkAssignCountry,
  bulkSetActive,
  directoryQuery,
  fetchTechnicians,
  hasActiveFilters,
  TECHNICIAN_ORDER_BY,
  type TechnicianFilters,
  type TechnicianListItem,
  type TechnicianOrderBy,
} from 'api/techniciansApi';
import ReferentialPageHeader from 'pages/AdminReferentials/components/ReferentialPageHeader';
import ReferentialStats from 'pages/AdminReferentials/components/ReferentialStats';
import { UserImportDialog } from 'pages/AdminDashboard/components/UserImportDialog';
import { downloadBlob } from 'utils/downloadBlob';
import TechniciansFiltersBar from './components/TechniciansFiltersBar';
import { getTechniciansColumns } from './components/columns';
import { buildTechniciansCsv } from './csv';

const PAGE_SIZE = 20;
/** The export replays the query in one shot; past this the user is told rather than truncated. */
const EXPORT_LIMIT = 1000;
const DEFAULT_SORT = { orderBy: 'name' as TechnicianOrderBy, order: 'asc' as const };

/** The three actions a selection offers. The country one carries its target into the dialog. */
type PendingAction = { kind: 'deactivate' | 'reactivate' } | { kind: 'country'; countryCode: string };

/**
 * Read the view off the URL, validating everything: a hand-edited query string must degrade to
 * the default view, never to a 400 from the API.
 */
const parseFilters = (params: URLSearchParams): TechnicianFilters => {
  /**
   * Positive, and deduplicated. Positive because `Number.isInteger` alone accepts -1 and 0, which
   * the server's id regex refuses — and this function exists so a hand-edited URL never becomes a
   * 400. Deduplicated because both zod schemas do it, and `?specialtyId=1,1` otherwise rendered
   * the same chip twice and echoed the duplicate straight back into the URL.
   */
  const numbers = (key: string): number[] | undefined => {
    const values = params
      .get(key)
      ?.split(',')
      .map(Number)
      .filter((value) => Number.isInteger(value) && value > 0);
    return values?.length ? Array.from(new Set(values)) : undefined;
  };
  const codes = params.get('countryCode')?.split(',').filter((code) => /^[A-Za-z]{2}$/.test(code));
  const countryCode = codes?.length ? Array.from(new Set(codes.map((c) => c.toUpperCase()))) : undefined;
  const orderBy = params.get('orderBy') as TechnicianOrderBy | null;
  const status = params.get('status');
  const availability = params.get('availability');
  const page = Number(params.get('page'));

  return {
    search: params.get('search')?.trim() || undefined,
    countryCode,
    geoAreaId: numbers('geoAreaId'),
    specialtyId: numbers('specialtyId'),
    expertiseId: numbers('expertiseId'),
    status: status === 'active' || status === 'inactive' ? status : undefined,
    // A hand-edited query string must degrade to the default view, never to a 400.
    availability: AVAILABILITY_STATES.includes(availability as AvailabilityState)
      ? (availability as AvailabilityState)
      : undefined,
    orderBy: orderBy && TECHNICIAN_ORDER_BY.includes(orderBy) ? orderBy : DEFAULT_SORT.orderBy,
    order: params.get('order') === 'desc' ? 'desc' : 'asc',
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    pageSize: PAGE_SIZE,
  };
};

/** The drawn empty state: a magnifier in a rounded square, then the two lines. */
const AdminTechnicians = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { currentUser } = useContext(AppContext);
  const canManage = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_TECHNICIANS_MANAGE);
  // The status endpoints below are users routes and guard on `dashboard:users:manage`. An admin
  // holding technicians alone keeps the selection and loses those two buttons, rather than being
  // shown a button that answers 403.
  const canManageUsers = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_USERS_MANAGE);

  /**
   * The view lives in the URL, not in `useState`: `App.js` mounts `<Routes key={pathname}>`, so
   * this subtree is unmounted on every navigation and state would lose the filters on the way
   * back from a record — the ops loop itself. `replace` keeps typing out of the history.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const apply = useCallback(
    (next: TechnicianFilters) => setSearchParams(directoryQuery(next), { replace: true }),
    [setSearchParams]
  );

  // Page 1 in the same update: an effect would pair the new filters with the previous page
  const updateFilters = useCallback(
    (partial: Partial<TechnicianFilters>) => apply({ ...filters, ...partial, page: 1 }),
    [apply, filters]
  );

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [running, setRunning] = useState(false);

  // A selection is a set of row indexes, so it means other technicians on another page and under
  // other filters: it goes when the query does rather than acting on rows nobody picked.
  useEffect(() => setRowSelection({}), [filters]);

  const { data, isLoading, isFetching, error, refetch } = useTechniciansList(filters);
  const technicians = data?.technicians ?? [];
  const total = data?.total ?? 0;

  const sorting = useMemo<SortingState>(
    () => [{ id: filters.orderBy, desc: filters.order === 'desc' }],
    [filters.orderBy, filters.order]
  );

  // A third click clears the sort; a server list still needs one, so it falls back to the default
  const onSortingChange = (next: SortingState) => {
    const [first] = next;
    updateFilters(
      first
        ? { orderBy: first.id as TechnicianOrderBy, order: first.desc ? 'desc' : 'asc' }
        : DEFAULT_SORT
    );
  };

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const full = await fetchTechnicians({ ...filters, page: 1, pageSize: EXPORT_LIMIT });
      if (full.total > full.technicians.length) {
        toast.error(
          t('Only the first {{count}} rows were exported. Narrow the filters to get the rest.', {
            count: full.technicians.length,
          })
        );
      }
      downloadBlob(
        new Blob([buildTechniciansCsv(t, full.technicians)], { type: 'text/csv;charset=utf-8;' }),
        `technicians-${new Date().toISOString().slice(0, 10)}.csv`
      );
    } catch (exportError) {
      toast.error(apiErrorMessage(t, exportError));
    } finally {
      setExporting(false);
    }
  };

  const openRecord = useCallback(
    (technician: TechnicianListItem) => navigate(`/admin/technicians/${technician.id}`),
    [navigate]
  );

  // The pencil promises a modification, so it says so through the location state the record reads
  // — not a `?edit=`, which would make "Annuler" a history entry and a browser Back a reopened draft.
  const editRecord = useCallback(
    (technician: TechnicianListItem) =>
      navigate(`/admin/technicians/${technician.id}`, { state: { edit: true } }),
    [navigate]
  );

  const columns = useMemo(
    () => getTechniciansColumns(t, canManage, openRecord, editRecord),
    [t, canManage, openRecord, editRecord]
  );

  // Keys are row indexes, so the rows come from the page currently rendered — twenty at most,
  // which is why this is not memoised: a `useMemo` here would only pin `technicians`'s identity.
  const selected = Object.keys(rowSelection).map((index) => technicians[Number(index)]).filter(Boolean);
  const selectedActive = selected.filter((technician) => technician.isActive);
  const selectedInactive = selected.filter((technician) => !technician.isActive);

  // Both referentials are already in the query cache — the filter bar above loads them — so the
  // cascade is counted from memory rather than from a round trip the confirmation would wait on.
  const { data: countries } = useCountries();
  const { data: subdivisions } = useSubdivisions();
  const countryOptions = (countries ?? []).filter((country) => country.is_active);
  const countryName = (code: string) => countryOptions.find((country) => country.code === code)?.name ?? code;

  const areaCountry = useMemo(
    () => new Map((subdivisions ?? []).map((area) => [area.id, area.country_code])),
    [subdivisions]
  );

  /**
   * What the confirmation states before anything is written: who changes country, and whose areas
   * the server's cascade will drop. An area this map does not know counts as outside, which is
   * exactly what the server's `country_code <> $2` does with it.
   */
  const cascade = (countryCode: string) => {
    const outside = (technician: TechnicianListItem) =>
      technician.geoAreas.filter((area) => areaCountry.get(area.id) !== countryCode);
    const losing = selected.filter((technician) => outside(technician).length > 0);
    return {
      changing: selected.filter((technician) => technician.profile.countryCode !== countryCode).length,
      losing: losing.length,
      areas: losing.reduce((total, technician) => total + outside(technician).length, 0),
    };
  };

  const targetIds = (action: PendingAction): number[] => {
    if (action.kind === 'deactivate') return selectedActive.map(({ id }) => id);
    if (action.kind === 'reactivate') return selectedInactive.map(({ id }) => id);
    return selected.map(({ id }) => id);
  };

  /**
   * Every bulk action reports what the server really wrote, never a blanket success: a batch that
   * moved three of five says so, and the list is refetched on the real state either way.
   */
  const runBulk = async (action: PendingAction) => {
    if (running) return; // a double click on the confirmation must send one request, not two
    const ids = targetIds(action);
    setRunning(true);
    try {
      const outcome =
        action.kind === 'country'
          ? await bulkAssignCountry(ids, action.countryCode)
          : await bulkSetActive(action.kind, ids);
      const done = outcome.updated.length;
      if (done < ids.length) {
        toast.error(
          t('{{done}} of {{total}} technicians were updated; the others were refused.', {
            done,
            total: ids.length,
          })
        );
      } else if (action.kind === 'country') {
        toast.success(
          t('Country assigned to {{count}} technician, {{areas}} areas cleared', {
            count: done,
            areas: outcome.clearedAreas,
          })
        );
      } else if (action.kind === 'deactivate') {
        toast.success(t('Technician deactivated', { count: done }));
      } else {
        toast.success(t('Technician reactivated', { count: done }));
      }
      setRowSelection({});
    } catch (bulkError) {
      toast.error(apiErrorMessage(t, bulkError));
    } finally {
      setRunning(false);
      setPendingAction(null);
      refetch();
    }
  };

  /** The one dialog the three actions share: its words, and the count its confirm button carries. */
  const confirmation = (action: PendingAction) => {
    if (action.kind === 'country') {
      const counts = cascade(action.countryCode);
      return {
        title: t('Assign {{country}} to the selection?', { country: countryName(action.countryCode) }),
        description: t(
          '{{changing}} technicians change country, {{losing}} of them lose their scope ({{areas}} areas).',
          counts
        ),
        confirmLabel: t('Assign the country'),
      };
    }
    const count = action.kind === 'deactivate' ? selectedActive.length : selectedInactive.length;
    return action.kind === 'deactivate'
      ? {
          title: t('Are you sure you want to deactivate {{count}} user?', { count }),
          description: t('These users can no longer log in. You can reactivate them later.'),
          confirmLabel: t('Deactivate ({{count}})', { count }),
        }
      : {
          title: t('Are you sure you want to reactivate {{count}} user?', { count }),
          description: t('These users will be able to log in again.'),
          confirmLabel: t('Reactivate ({{count}})', { count }),
        };
  };

  const stats = data?.stats;
  const cards = [
    {
      key: 'total',
      label: t('Technicians'),
      value: stats?.total ?? null,
      icon: Wrench,
      hint: t('Every technician you can see'),
      // No `?? 0`: a failed read knows nothing, and "0 of them are active" would be a claim.
      tooltip: stats ? t('Deactivated accounts are included: {{active}} of them are active.', { active: stats.active }) : undefined,
    },
    {
      key: 'available',
      label: t('Available today'),
      value: stats?.availableToday ?? null,
      icon: CalendarCheck,
      hint: stats ? t('{{count}} unavailable right now', { count: stats.unavailableToday }) : undefined,
      tooltip: t('Active accounts with no declared absence covering today. Availability is derived from what the technician declares from the mobile app; it is not a status, and an unavailable technician keeps an active account.'),
    },
    {
      key: 'areas',
      label: t('Areas covered'),
      value: stats?.areasCovered ?? null,
      icon: MapIcon,
      hint: t('Distinct areas at least one active technician covers'),
    },
    {
      key: 'rating',
      label: t('Average rating'),
      value: stats?.ratingAvg ?? null,
      icon: Star,
      hint: t('Across every intervention rated so far'),
      tooltip: t('An average over zero ratings is not zero — it does not exist, so the card shows a dash.'),
    },
  ];

  return (
    <div className="container mx-auto space-y-4 p-6">
      <ReferentialPageHeader
        icon={Wrench}
        title={t('Technicians')}
        subtitle={t('The pool of field technicians, their scope and their skills')}
        actionLabel={canManage ? t('Add a technician') : undefined}
        onAction={canManage ? () => navigate('/admin/technicians/new') : undefined}
        secondaryAction={
          canManage ? (
            <Button variant="outline" onClick={() => setImporting(true)}>
              <Upload className="mr-1 h-4 w-4" />
              {t('Import technicians')}
            </Button>
          ) : undefined
        }
      />

      <ReferentialStats stats={cards} loading={isLoading} />

      {/* One card, as drawn: the toolbar is its header, the table sits flush, the pager is its
          last row. No overflow rule: the MultiSelect menus are absolute, not portalled. */}
      <Card className="rounded-[14px]">
        <div className="border-b px-5 py-4">
          <TechniciansFiltersBar
            filters={filters}
            onChange={updateFilters}
            // Resets the filters, not the sort: the user asked to clear what they narrowed.
            onReset={() =>
              apply({ orderBy: filters.orderBy, order: filters.order, page: 1, pageSize: PAGE_SIZE })
            }
            onExport={exportCsv}
            exporting={exporting}
            canExport={total > 0}
          />
        </div>

        {isLoading ? (
          <div className="space-y-2 p-5">
            {[0, 1, 2, 3, 4].map((row) => (
              <Skeleton key={row} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <p className="p-5 text-sm text-red-600">{apiErrorMessage(t, error)}</p>
        ) : (
          <DataTable
            embedded
            data={technicians}
            columns={columns}
            // Both are client-side in DataTable, so both would act on one page only
            showGlobalSearch={false}
            showExport={false}
            // A withdrawn account stays readable but recedes; it is not a live choice.
            // Lighter recede in dark: opacity-60 over muted text on near-black is unreadable.
            rowClassName={(row: TechnicianListItem) =>
              row.isActive ? undefined : 'opacity-60 dark:opacity-80'
            }
            emptyMessage={
              hasActiveFilters(filters) ? (
                <EmptyState
                  title={t('No technician found')}
                  hint={t('Adjust your search or your filters.')}
                />
              ) : (
                <EmptyState title={t('No technician yet')} />
              )
            }
            // The row opens the record for reading; the pencil, the checkbox and the name button
            // own their own click, which DataTable's guard keeps out of this handler.
            onRowClick={openRecord}
            enableRowSelection={canManage}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            canSelectRow={(row: TechnicianListItem) => row.canEdit}
            bulkActionsBar={() => (
              <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-5 py-3">
                <span className="text-[13px] font-medium">
                  {t('{{count}} user selected', { count: selected.length })}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRowSelection({})}>
                    <X className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                    {t('Deselect')}
                  </Button>
                  {/* An action, not a filter: the trigger is pinned to the empty value so it keeps
                      reading "Affecter un pays" instead of the last country picked. */}
                  <Select value="" onValueChange={(code) => setPendingAction({ kind: 'country', countryCode: code })}>
                    <SelectTrigger
                      aria-label={t('Assign a country')}
                      disabled={running || countryOptions.length === 0}
                      className="h-8 w-[188px] gap-2 rounded-md text-[13px]"
                    >
                      <SelectValue
                        placeholder={
                          countryOptions.length === 0
                            ? t('No country in the referential yet')
                            : t('Assign a country')
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {countryOptions.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {canManageUsers && selectedActive.length > 0 && (
                    <Button variant="outline" size="sm" disabled={running} onClick={() => setPendingAction({ kind: 'deactivate' })}>
                      <UserX className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                      {t('Deactivate ({{count}})', { count: selectedActive.length })}
                    </Button>
                  )}
                  {canManageUsers && selectedInactive.length > 0 && (
                    <Button size="sm" disabled={running} onClick={() => setPendingAction({ kind: 'reactivate' })}>
                      <UserCheck className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                      {t('Reactivate ({{count}})', { count: selectedInactive.length })}
                    </Button>
                  )}
                </div>
              </div>
            )}
            pageSize={PAGE_SIZE}
            serverSorting={{ sorting, onSortingChange }}
            serverPagination={{
              pageIndex: filters.page - 1,
              pageCount: Math.max(1, Math.ceil(total / PAGE_SIZE)),
              totalCount: total,
              hasNextPage: filters.page * PAGE_SIZE < total,
              busy: isFetching,
              onPreviousPage: () => apply({ ...filters, page: Math.max(1, filters.page - 1) }),
              onNextPage: () => apply({ ...filters, page: filters.page + 1 }),
            }}
          />
        )}
      </Card>

      {/* A footnote under the card, not in it: two of the eleven drawn columns still have no
          source. Availability left this list when L3 gave it a table. */}
      <p className="text-xs text-muted-foreground">
        {t('Interventions and Last intervention are not shown: their data does not exist yet.')}
      </p>

      {pendingAction && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          loading={running}
          {...confirmation(pendingAction)}
          onClose={() => setPendingAction(null)}
          onConfirm={() => runBulk(pendingAction)}
        />
      )}

      {importing && (
        <UserImportDialog
          open
          onClose={() => setImporting(false)}
          onUsersImported={refetch}
          // Pre-scoped, as this screen already was: the dialog carries its own technician branch.
          allowedRoles={['technician']}
        />
      )}
    </div>
  );
};

export default AdminTechnicians;
